import {Crosslink} from './crosslink';
import {TourFeatures} from './tour-features';
import {Section, SectionType} from './section';
import {BuilderFeature} from './builder-features';
import {ModelInfo} from './model-info';
import {Scene} from './scene';
import {Video} from './video';
import {ContactInfo} from './contact-info';
import {AboutInfo} from './about-info';
import {CommunityInfo} from './community-info';
import {FeedbackInfo} from './feedback-info';

export enum TourVideoType {
  Floorplan = <any>'Floorplan',
  Fullframe = <any>'Fullframe'
}

export enum PlaybackQuality {
  HD = <any>'HD',
  High = <any>'High',
  Medium = <any>'Medium',
  Low = <any>'Low'
}

export class Tour {

  bandwidthImageURL: string;
  bandwidthImageSize: number;
  title: string;
  subTitle: string;
  crosslink: Crosslink;
  features: TourFeatures;
  feedbackInfo: FeedbackInfo;

  modelInfo: ModelInfo;
  sceneList: Scene[] = [];
  displaySections: Section[] = [];
  tourVideo: Video;
  preferredPlaybackQuality = PlaybackQuality.High;
  preferredPlaybackType: TourVideoType = TourVideoType.Fullframe;

  aboutHomeInfo: AboutInfo;
  communityInfo: CommunityInfo;
  contactInfo: ContactInfo;
  builderFeatures: BuilderFeature[];

  constructor(public data: any) {
    const json: JSON = data['main'];

    this.features = new TourFeatures(json['appFeatures']);
    this.bandwidthImageSize = json['bandwidthImage']['size'];
    this.bandwidthImageURL = json['bandwidthImage']['url'];
    if (json['crosslink']) {
      this.crosslink = new Crosslink(json['crosslink']);
    }

    if (json['modelInfo']) {
      this.modelInfo = new ModelInfo(json['modelInfo']);
    }
    if (json['video']) {
      this.tourVideo = new Video(json['video']);
    }

    // not every tour has a 'titles/main' element
    if (json['titles']) {
      this.title = json['titles']['main'];
    } else {
      this.title = this.modelInfo.modelName;
    }

    for (const s of json['scenesMain']['scenes']) {
      this.sceneList.push(new Scene(s));
    }

    for (const s of json['scenesDisplay']['sections']) {
      this.displaySections.push(new Section(s, SectionType.Scenes));
    }
  }

  findOption(sceneId: number, groupId: number, optionId: number) {
    for (const s of this.sceneList) {
      if (s.id === sceneId) {
        for (const og of s.optionGroups) {
          if (og.id === groupId) {
            for (const o of og.options) {
              if (o.id === optionId) {
                return o;
              }
            }
          }
        }
      }
    }
    return null;
  }

  findSceneByIndex(index: number) {
    if (this.sceneList.length > index) {
      const scene = this.sceneList[index];
      if (scene) {
        return scene;
      }
    }
    return null;
  }

  findSceneByDisplayName(sceneName: string) {
    for (const scene of this.sceneList) {
      if (scene.displayName === sceneName) {
        return scene;
      }
    }
    return null;
  }

  findSceneById(id: number) {
    for (const scene of this.sceneList) {
      if (scene.id === id) {
        return scene;
      }
    }
    return null;
  }

  findOptionGroupById(id: number, sceneId: number) {
    for (const scene of this.sceneList) {
      if (scene.id === sceneId) {
        for (const group of scene.optionGroups) {
          if (group.id === id) {
            return group;
          }
        }
      }
    }
    return null;
  }

  findSceneForCurrentTime(time: number) {
    for (const scene of this.sceneList) {
      if (time > scene.startTime && time < scene.endTime) {
        // console.log('found: ' + scene.displayName);
        return scene;
      }
    }
    return null;
  }

  findSceneAfterScene(scene: Scene) {
    for (let i = 0; i < this.sceneList.length; i++) {
      const s: Scene = this.sceneList[i];
      if (s.id === scene.id && this.sceneList.length >= i + 1) {
        return this.sceneList[i + 1];
      }
    }
    return null;
  }

  hasVideoTours() {
    return ((this.tourVideo.encodings !== null && this.tourVideo.encodings.length > 0)
            || (this.tourVideo.fullFrameEncodings !== null && this.tourVideo.fullFrameEncodings.length > 0));
  }

  hasWeb360Tour() {
    return (this.hasMatterport360() || this.hasGeneric360());
  }

  hasMatterport360() {
    return (this.crosslink && this.crosslink.url !== '' && (this.crosslink.url.indexOf('matterport.com') > -1));
  }

  hasGeneric360() {
    return (this.crosslink && this.crosslink.url !== '' && (this.crosslink.url.indexOf('https:') === 0));
  }

  isWeb360Only() {
    return (!this.hasVideoTours() && this.hasWeb360Tour());
  }
}



// WEBPACK FOOTER //
// ./src/app/model/tour.ts